import PubSub from 'pubsub-js';
import { Userpilot } from 'userpilot';
import { useUserContext } from '../contexts/UserContext';
import { useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { getAppState } from '../redux/store';
import { getOrGenerateEpochUUID, wait } from '../helpers/jsHelper';
import {
  resetUserpilotActionStates,
  selectUserpilotOnboardingInfo,
  selectUserpilotState,
  setUserpilotOnboardingInfo,
  setUserpilotActionStates,
  setUserpilotSavedSteps,
} from '../redux/slicers/activitySlicer';
import { useDispatch, useSelector } from 'react-redux';
import { sendUnityEvent } from '../components/container/unityContainer';
import { curatorActions, selectRoomLoadedCompletely } from '../redux/slicers/admin/curatorSlicer';
import { USERPILOT_FUNCTIONS } from '../constants/constants';
import { UnityEvent } from '../helpers/unityPubSub';
import { debounce } from 'lodash';
import { dispatcher } from '../helpers/projectHelper';
import { apiGetUserpilotSteps } from '../helpers/api';
import { fetchAllPages } from '../helpers/apiHelper';
import { LogConsole } from '../helpers/logEvents';
import { closeSidebar, toggleSidebarSection } from '../helpers/curatorActions';
import { CURATOR_MENU_SECTION } from '../redux/slicers/admin/curatorMenuPreferencesSlicer';
import { setActiveTab, TAB_NAME } from '../redux/slicers/admin/curatorStylesSlicer';
import { isUserOnboarding } from '../components/gamificationTooltip/helpers';
import {
  getBasicUserDetails,
  getUserpilotStepState,
  USERPILOT_PRODUCTION_TOKEN,
  USERPILOT_STAGING_TOKEN,
  userPilotEvents,
} from '../common/Userpilot/userpilotHelper';
import { isProductionUrl } from '../helpers/urlHelper';
import { CURRENT_USER, EG_SUBMISSION } from '../constants/projectConstants';
import { isPublicPageView, isRetailAutomation } from '../components/Configurator/utils';
import { isRetailConfiguratorPage } from '../components/admin/curator/ProductBuilderComponents/commonHelper';

export const fetchUserpilotSteps = async (params = '') => {
  const response = await fetchAllPages(
    async (page) => await apiGetUserpilotSteps({ param: `?${params}&page=${page}` })
  );
  if (!response || response?.results?.length === 0) {
    return null;
  }

  const _steps = response?.results.map(
    ({ id, token, name, step_number, is_object, argument_data, argument_text }) => ({
      id,
      token,
      action: name,
      step: step_number,
      details: is_object ? argument_data : argument_text || '',
      isObjectSelected: is_object,
    })
  );

  const data = {};
  _steps.forEach((item) => {
    const { token, step, action, details } = item;

    if (!data?.[token]) {
      data[token] = {
        [step]: {
          action,
          details,
        },
      };
    } else {
      if (data?.[token]?.[step]) {
        const _steps = Array.isArray(data[token][step])
          ? [...data[token][step]]
          : [data[token][step]];

        _steps.push({
          action,
          details,
        });
        data[token][step] = _steps;
      } else {
        data[token][step] = {
          action,
          details,
        };
      }
    }
  });

  dispatcher(setUserpilotSavedSteps(data));

  return _steps;
};

// Initialize Userpilot
if (!isPublicPageView() && (localStorage.getItem(CURRENT_USER) || localStorage.getItem(EG_SUBMISSION))) {
  Userpilot.initialize(isProductionUrl ? USERPILOT_PRODUCTION_TOKEN : USERPILOT_STAGING_TOKEN); // rachit@imagine.io account
}

export default function useUserPilot() {
  const dispatch = useDispatch();
  const location = useLocation();
  const { currentUser } = useUserContext();
  const {
    trackPageViewEvent,
    trackOnboardingDropped,
    trackOnboardingFinished,
    trackOnboardingStarted,
  } = userPilotEvents();
  const [currentStepData, setCurrentStepData] = useState(null);
  const isOnboardingState = isUserOnboarding();
    const roomLoadedCompletely = useSelector(selectRoomLoadedCompletely);

  useEffect(() => {
    if (!isOnboardingState) {
      Userpilot.identify(
        currentUser?.user_pilot_id || getOrGenerateEpochUUID(), // identifying user by user_pilot_id ( if available ) otherwise user id
        getBasicUserDetails()
      );
    }
  }, [isOnboardingState]);

  useEffect(() => {
    if (currentUser && !isOnboardingState && roomLoadedCompletely) {
      if (!isRetailAutomation() && !isRetailConfiguratorPage()) {
        fetchUserpilotSteps();
      }
    }
  }, [isOnboardingState, roomLoadedCompletely]);

  useEffect(() => {
    if (isOnboardingState) {
      return;
    }
    if (currentUser) {
      Userpilot.reload();
    }

    // trackPageViewEvent();
  }, [currentUser, location, isOnboardingState]);

  /**
   * Sends a Unity event to get the screen position of an object.
   * @param {object} [data={}] - The data to be sent to Unity.
   * @returns {void}
   */
  const getObjectScreenPosition = (data = null) => {
    // If the current step data is a HighlightObjectReceiver event, use the details of that event.
    const _data =
      currentStepData?.action === USERPILOT_FUNCTIONS.HighlightObjectReceiver
        ? currentStepData?.details
        : data;

    if (_data && _data?.objectName) {
      // Send the GetObjectScreenPositionReceiver event to Unity with the data.
      sendUnityEvent('OnBoardingRoomModule', 'GetObjectScreenPosition', _data?.objectName);
    }
  };

  const doAction = async (stepAction) => {
    if (!stepAction) return;
    switch (stepAction?.action) {
      case USERPILOT_FUNCTIONS.OnCameraRotate:
        dispatch(setUserpilotActionStates({ isCameraRotate: true }));
        break;
      case USERPILOT_FUNCTIONS.OnCameraPan:
        dispatch(setUserpilotActionStates({ isCameraPan: true }));
        break;
      case USERPILOT_FUNCTIONS.OnCameraZoomInOut:
        dispatch(setUserpilotActionStates({ isCameraZoomInOut: true }));
        break;
      case USERPILOT_FUNCTIONS.HighlightObjectReceiver:
        if (stepAction?.details) {
          dispatch(setUserpilotActionStates({ isPropHighlighted: true }));
          // await wait(100);
          getObjectScreenPosition(stepAction?.details);
          // sendUnityEvent('OnBoardingRoomModule', 'SetOnBoardingRoomReceiver', 'true');
          await wait(500);
          setCurrentStepData({
            action: USERPILOT_FUNCTIONS.HighlightObjectReceiver,
            details: stepAction?.details,
          });
          sendUnityEvent(
            'OnBoardingRoomModule',
            'HighlightObjectReceiver',
            JSON.stringify(stepAction?.details)
          );

          sendUnityEvent(
            'OnBoardingRoomModule',
            'OnFocusHighLightObject',
            JSON.stringify({ objectName: stepAction?.details?.objectName, focusOffset: 2.0 }) // default: 2.0
          );
        }
        break;
      case USERPILOT_FUNCTIONS.SelectObjectReceiver:
        if (stepAction?.details) {
          await wait(100);
          dispatch(setUserpilotActionStates({ isPropSelected: true }));
          sendUnityEvent('OnBoardingRoomModule', 'StopBlinkingReceiver');
          sendUnityEvent(
            'OnBoardingRoomModule',
            'SelectObjectReceiver',
            JSON.stringify(stepAction?.details)
          );
        }
        break;
      case USERPILOT_FUNCTIONS.FocusObjectReceiver:
        await wait(100);
        dispatch(setUserpilotActionStates({ isPropFocused: true }));
        sendUnityEvent('ShortcutsModule', 'FocusObjectReceiver');
        break;
      case USERPILOT_FUNCTIONS.StartGizmoBlinking:
        if (stepAction?.details) {
          await wait(100);
          dispatch(setUserpilotActionStates({ isGizmoHighlighted: true }));
          sendUnityEvent('OnBoardingRoomModule', 'StartGizmoBlinking', stepAction?.details);
        }
        break;
      case USERPILOT_FUNCTIONS.OnClickSelectAxisButtonReceiver:
        if (stepAction?.details) {
          await wait(100);
          dispatch(setUserpilotActionStates({ isAxisSelected: true }));
          sendUnityEvent('ShortcutsModule', 'OnClickSelectAxisButtonReceiver', stepAction?.details);
          dispatch(curatorActions.toggleSelectedAxis(stepAction?.details));
        }
        break;

      case USERPILOT_FUNCTIONS.OpenProductsTab:
        toggleSidebarSection(CURATOR_MENU_SECTION.PRODUCTS);
        break;
      case USERPILOT_FUNCTIONS.OpenStyleTab:
        toggleSidebarSection(CURATOR_MENU_SECTION.STYLE);
        break;
      case USERPILOT_FUNCTIONS.OpenCamerasTab:
        toggleSidebarSection(CURATOR_MENU_SECTION.CAMERAS);
        break;
      case USERPILOT_FUNCTIONS.OpenLightTab:
        toggleSidebarSection(CURATOR_MENU_SECTION.LIGHT);
        break;
      case USERPILOT_FUNCTIONS.OpenImagesTab:
        toggleSidebarSection(CURATOR_MENU_SECTION.RENDERS_AND_PREVIEWS);
        break;
      case USERPILOT_FUNCTIONS.OpenSettingsTab:
        toggleSidebarSection(CURATOR_MENU_SECTION.SETTINGS);
        break;
      case USERPILOT_FUNCTIONS.OpenMetadataTab:
        toggleSidebarSection(CURATOR_MENU_SECTION.METADATA);
        break;
      case USERPILOT_FUNCTIONS.OpenTileAndGroutTab:
        toggleSidebarSection(CURATOR_MENU_SECTION.TILE_GROUT);
        break;
      case USERPILOT_FUNCTIONS.OpenAutomationTab:
        toggleSidebarSection(CURATOR_MENU_SECTION.AUTOMATION);
        break;
      case USERPILOT_FUNCTIONS.OpenMoreTab:
        toggleSidebarSection(CURATOR_MENU_SECTION.MORE);
        break;
      case USERPILOT_FUNCTIONS.CloseCuratorSidebarSection:
        closeSidebar();
        break;
      case USERPILOT_FUNCTIONS.OpenStyleTextureTab:
        // toggleSidebarSection(CURATOR_MENU_SECTION.STYLE);
        dispatch(setActiveTab(TAB_NAME.TEXTURES));
        break;
      case USERPILOT_FUNCTIONS.OpenStyleMaterialTab:
        // toggleSidebarSection(CURATOR_MENU_SECTION.STYLE);
        dispatch(setActiveTab(TAB_NAME.MATERIALS));
        break;
      case USERPILOT_FUNCTIONS.OpenStyleColorTab:
        // toggleSidebarSection(CURATOR_MENU_SECTION.STYLE);
        dispatch(setActiveTab(TAB_NAME.COLORS));
        break;
      case USERPILOT_FUNCTIONS.OpenStylePropertiesTab:
        // toggleSidebarSection(CURATOR_MENU_SECTION.STYLE);
        dispatch(setActiveTab(TAB_NAME.PROPERTIES));
        break;

      default:
        dispatch(resetUserpilotActionStates());
    }
  };

  useEffect(() => {
    const listener = debounce(() => {
      getObjectScreenPosition(currentStepData);
    }, 60);
    window.addEventListener('resize', listener);
    return () => {
      window.removeEventListener('resize', listener);
      listener.cancel();
    };
  }, [currentStepData]);

  useEffect(() => {
    /**
     * This callback is called when the `OnReceiveObjectScreenPosition` event is received
     * from Unity. It calculates the position of the Userpilot tooltip based on the
     * received coordinates and updates the tooltip's position.
     */
    const OnReceiveObjectScreenPositionCallback = (_, data) => {
      console.warn('onReceiveObjectScreenPositionCallback', data);
      if (data.length) {
        const { x, y } = data?.[0] || data?.[1];
        const unityEle = document.querySelector('#unityCanvasContainer');
        const unityWidth = unityEle.offsetWidth;
        const unityHeight = unityEle.offsetHeight;
        const coordinates = {
          left: unityWidth * parseFloat(x).toFixed(3) + 96,
          top: unityHeight - unityHeight * parseFloat(y).toFixed(3) + 56,
        };

        LogConsole('DEBUG__coordinates', {
          ...coordinates,
          x,
          y,
          unityWidth,
          unityHeight,
        });

        const userpilotCustomTooltip = document.querySelector('#userpilot-custom-tooltip');
        if (userpilotCustomTooltip) {
          userpilotCustomTooltip.style.left = coordinates.left + 'px';
          userpilotCustomTooltip.style.top = coordinates.top + 'px';
        }
      }
    };

    PubSub.subscribe(
      UnityEvent.OnReceiveObjectScreenPosition,
      OnReceiveObjectScreenPositionCallback
    );

    return () => PubSub.unsubscribe(OnReceiveObjectScreenPositionCallback);
  }, []);

  useEffect(() => {
    if (!currentUser) return;

    // trackUserPilotEvent('SelectObject');

    Userpilot.on('started', function (event) {
      LogConsole('Userpilot_Experience started', event);

      trackOnboardingStarted({
        flow_id: event.id,
        flow_name: event.name,
        // TODO: Add more data here
      });

      dispatch(
        setUserpilotOnboardingInfo({
          id: event.id,
          name: event.name,
          token: event.token,
          isRunning: true,
        })
      );
    });

    Userpilot.on('step', async function (event) {
      // lastStep = event.step;

      const stepDetails = await getUserpilotStepState();
      const tokenDetails = stepDetails?.[event.token];
      const stepAction = tokenDetails?.[event.step];

      const { isPropHighlighted } = selectUserpilotState(getAppState());

      dispatch(
        setUserpilotOnboardingInfo({
          step_id: event.step_id,
          step_number: event.step,
        })
      );

      LogConsole('Userpilot_Experience step', {
        event,
        currentStep: event.step,
        stepDetails,
        tokenDetails,
        stepAction,
      });

      if (isPropHighlighted) sendUnityEvent('OnBoardingRoomModule', 'StopBlinkingReceiver');
      sendUnityEvent('OnBoardingRoomModule', 'SetOnBoardingRoomReceiver', 'false');

      if (stepDetails && tokenDetails && stepAction) {
        if (Array.isArray(stepAction)) {
          stepAction.forEach((action) => doAction(action));
        } else doAction(stepAction);
      } else {
        dispatch(resetUserpilotActionStates());
      }

      // if (event.step === 1) {
      //   // Send Select Event to unity with a random object.
      //   unityContext.send('RoomModule', 'GetRoomInfo', 'normal');
      //   if (window.roomInfo) {
      //     LogConsole('UP_Got Room Info', window.roomInfo);
      //     const blenderAssetBundleDatas = window.roomInfo.blenderAssetBundleDatas;
      //     const randomIndex = Math.floor(Math.random() * blenderAssetBundleDatas.length);
      //     let objectName = blenderAssetBundleDatas[randomIndex].objectName;
      //     window.tempObjectName = objectName;
      //     LogConsole('UP_We will send Unity Select Object for this object name: ', objectName);
      //     const data = {
      //       objectName: objectName,
      //       showOutline: true,
      //     };
      //     unityContext.send('OnBoardingRoomModule', 'HighlightObjectReceiver', JSON.stringify(data));
      //   }
      // }
    });

    // const handleRightClick = (event) => {
    //   if (lastStep === 2 && event.button === 2) { // button 2 is for right-click
    //     LogConsole("UP_right click");
    //     Userpilot.next();
    //   }
    // };

    // window.addEventListener('contextmenu', handleRightClick);

    Userpilot.on('dismissed', function (event) {
      LogConsole('Userpilot_Experience Dismissed', event);

      const { id, name, step_id, step_number } = selectUserpilotOnboardingInfo(getAppState()) || {};

      trackOnboardingDropped({
        flow_id: id || event.id,
        flow_name: name || event.name,
        step_id,
        step_number,
        // TODO: Add more data here
      });
    });

    Userpilot.on('completed', function (event) {
      const { isPropHighlighted, isGizmoHighlighted } = selectUserpilotState(getAppState());
      LogConsole('Userpilot_Experience completed', event);
      if (isPropHighlighted) sendUnityEvent('OnBoardingRoomModule', 'StopBlinkingReceiver');
      if (isGizmoHighlighted) sendUnityEvent('OnBoardingRoomModule', 'StopGizmoBlinking');

      const { id, name, step_id, step_number } = selectUserpilotOnboardingInfo(getAppState()) || {};

      trackOnboardingFinished({
        flow_id: id || event.id,
        flow_name: name || event.name,
        step_id,
        step_number,
        // TODO: Add more data here
      });

      dispatch(setUserpilotOnboardingInfo(null));
    });

    // Cleanup event listener on component unmount
    // return () => {
    //   window.removeEventListener('contextmenu', handleRightClick);
    // };
  }, [currentUser]);

  return;
}
